interface IntervalNode<T> extends Interval {
  data: T[]; // Data associated with the interval
  max: number; // Maximum end value in the subtree
  left: IntervalNode<T> | null;
  right: IntervalNode<T> | null;
  height: number; // For balancing the tree
}

interface Interval {
  start: number; // Start of the interval
  end: number; // End of the interval
}

export class IntervalTree<T> {
  private root: IntervalNode<T> | null = null;

  /**
   * Insert an interval into the interval tree.
   */
  insert(start: number, end: number, data: T): void {
    this.root = this.insertNode(this.root, start, end, data);
  }

  /**
   * Query the interval tree for all intervals that overlap with a given range.
   */
  query(start: number, end: number, onSort?: (a: Interval, b: Interval) => number): T[] {
    const nodes: IntervalNode<T>[] = [];
    this.queryOverlapping(this.root, start, end, nodes);
    if (onSort) {
      nodes.sort(onSort);
    }
    return nodes.reduce((acc, { data }) => {
      acc.push(...data);
      return acc;
    }, []);
  }

  /**
   * Insert a node into the interval tree.
   */
  private insertNode(
    root: IntervalNode<T> | null,
    start: number,
    end: number,
    data: T,
  ): IntervalNode<T> {
    if (!root) {
      return {
        start,
        end,
        data: [data],
        max: end,
        left: null,
        right: null,
        height: 1,
      };
    }

    if (start < root.start) {
      root.left = this.insertNode(root.left, start, end, data);
    } else {
      root.right = this.insertNode(root.right, start, end, data);
    }

    // Update the max value and height
    root.max = Math.max(root.max, end);
    root.height = 1 + Math.max(this.getHeight(root.left), this.getHeight(root.right));

    return this.balanceTree(root);
  }

  /**
   * Query the tree for all intervals that overlap with a given range.
   */
  private queryOverlapping(
    root: IntervalNode<T> | null,
    start: number,
    end: number,
    result: IntervalNode<T>[],
  ): void {
    if (!root) {
      return;
    }

    // Check if the current node overlaps with the query range
    if (root.start <= end && root.end >= start) {
      result.push(root);
    }

    // Traverse the left subtree if it might contain overlapping intervals
    if (root.left && root.left.max >= start) {
      this.queryOverlapping(root.left, start, end, result);
    }

    // Traverse the right subtree
    this.queryOverlapping(root.right, start, end, result);
  }

  /**
   * Balance the interval tree.
   */
  private balanceTree(root: IntervalNode<T>): IntervalNode<T> {
    const balance = this.getBalanceFactor(root);

    if (balance > 1) {
      if (this.getBalanceFactor(root.left!) < 0) {
        root.left = this.rotateLeft(root.left!);
      }
      return this.rotateRight(root);
    }

    if (balance < -1) {
      if (this.getBalanceFactor(root.right!) > 0) {
        root.right = this.rotateRight(root.right!);
      }
      return this.rotateLeft(root);
    }

    return root;
  }

  /**
   * Rotate a subtree to the right.
   */
  private rotateRight(y: IntervalNode<T>): IntervalNode<T> {
    const x = y.left!;
    y.left = x.right;
    x.right = y;

    this.updateHeightAndMax(y);
    this.updateHeightAndMax(x);

    return x;
  }

  /**
   * Rotate a subtree to the left.
   */
  private rotateLeft(x: IntervalNode<T>): IntervalNode<T> {
    const y = x.right!;
    x.right = y.left;
    y.left = x;

    this.updateHeightAndMax(x);
    this.updateHeightAndMax(y);

    return y;
  }

  /**
   * Update the height and max value of a node.
   */
  private updateHeightAndMax(node: IntervalNode<T>): void {
    node.height = 1 + Math.max(this.getHeight(node.left), this.getHeight(node.right));
    node.max = Math.max(node.end, Math.max(this.getMax(node.left), this.getMax(node.right)));
  }

  /**
   * Get the height of a tree node.
   */
  private getHeight(node: IntervalNode<T> | null): number {
    return node ? node.height : 0;
  }

  /**
   * Get the max value of a tree node.
   */
  private getMax(node: IntervalNode<T> | null): number {
    return node ? node.max : Number.NEGATIVE_INFINITY;
  }

  /**
   * Get the balance factor of a tree node.
   */
  private getBalanceFactor(node: IntervalNode<T> | null): number {
    return node ? this.getHeight(node.left) - this.getHeight(node.right) : 0;
  }
}
